import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  IdentificationCard,
  ListChecks,
  Question,
  Circle,
  PencilSimple,
  FloppyDisk,
  CheckCircle,
  Warning,
  ArrowClockwise,
  Sliders,
  XCircle,
  MapTrifold,
  Sparkle,
  Trash,
  MagnifyingGlass,
  Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, api, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";

const OBJ_LETTERS = ["A", "B", "C", "D"];
const PERC_LETTERS = ["A", "B", "C", "D", "E"];

/* -------------------- Student-name review helpers -------------------- */
// The endpoint may return the cropped name as a data URL, a regular URL, or
// a small image descriptor.  Keeping this permissive makes the UI compatible
// with cards processed before and after the dedicated TPI review release.
const nameCropDataUrl = (value) => {
  if (typeof value === "string") {
    const text = value.trim();
    return /^(?:data:image\/|https?:\/\/|\/)/i.test(text) ? text : "";
  }
  if (!value || typeof value !== "object") return "";
  const direct = value.image_data_uri || value.data_url || value.dataUrl || value.url || value.src || value.image_url || value.imageUrl;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const encoded = value.base64 || value.png_b64 || value.preview_b64 || value.data;
  if (typeof encoded !== "string" || !encoded.trim()) return "";
  if (/^data:image\//i.test(encoded.trim())) return encoded.trim();
  const mimeType = String(value.mime_type || value.mime || "image/png").trim() || "image/png";
  return `data:${mimeType};base64,${encoded.trim().replace(/^base64,/i, "")}`;
};

const resolveStudentNameCrop = (correction, context) => {
  const ocr = correction?.student_name_ocr || {};
  const contextualOcr = context?.student_name_ocr || {};
  const options = [
    context?.student_name_crop,
    context?.name_crop,
    context?.name_crop_image,
    context?.ocr_name_crop,
    contextualOcr.crop,
    contextualOcr.student_name_crop,
    correction?.student_name_crop,
    correction?.name_crop,
    correction?.name_crop_image,
    ocr.crop,
    ocr.student_name_crop,
    ocr.crop_image,
  ];
  return options.map(nameCropDataUrl).find(Boolean) || "";
};

const isIdentityVisualReview = (correction, context) => {
  const ocr = correction?.student_name_ocr || {};
  if (
    context?.identity_visual_review
    || context?.tpi_name_review
    || context?.special_name_review
    || context?.is_student_identity_review_available
    || context?.is_tpi_name_review_available
    || context?.review_type === "student_identity_visual"
    || context?.review_type === "tpi_student_name"
    || context?.student_name_review?.kind === "tpi"
    || ocr?.tpi_name_review
    || ocr?.special_name_review
  ) return true;
  const layoutHints = [
    correction?.layout_id,
    correction?.layout?.id,
    correction?.layout?.name,
    correction?.layout_name,
    correction?.exam?.layout_id,
    correction?.exam?.layout?.id,
    context?.layout_id,
    context?.layout?.id,
    context?.layout_name,
    ocr?.layout_id,
  ];
  return layoutHints.some((value) => /(?:teste[\s_-]*de[\s_-]*progresso|\btpi\b)/i.test(String(value || "")));
};

const candidateNameSimilarityPercent = (student) => {
  const raw = Number(
    student?.name_match_percent
    ?? student?.name_similarity_percent
    ?? student?.match_percent
    ?? student?.name_match_similarity
    ?? student?.name_similarity
    ?? student?.match_score,
  );
  if (!Number.isFinite(raw) || raw < 0) return null;
  return Math.min(100, raw <= 1 ? raw * 100 : raw);
};

/* -------------------- Read-only chip row -------------------- */
const ReadChipRow = ({ questions, answers, prefix = "", examKey = null, annulled = null, diagnostics = null, reviewOnly = false, aiSuggestions = null }) => (
  <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
    {questions.map((q) => {
      const key = prefix ? `${prefix}${q}` : String(q);
      const confidence = !prefix ? diagnostics?.[String(q)] : null;
      const aiSuggestion = !prefix ? aiSuggestions?.[String(q)] : null;
      if (reviewOnly && !confidence?.review_required) return null;
      const val = answers[key];
      // Only compute correctness for objective (not perception)
      let tone = "";
      if (!prefix && examKey) {
        const expected = examKey[String(q)];
        const isAnnulled = annulled?.has?.(q);
        if (isAnnulled) tone = "border-muted-foreground/40 bg-muted/40";
        else if (!expected) tone = "";
        else if (!val) tone = "border-[hsl(var(--warning))]/60";
        else if (val === expected) tone = "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10";
        else tone = "border-destructive bg-destructive/10";
      }
      const confidenceTitle = confidence
        ? [`Confiança: ${Number(confidence.confidence_percent || 0).toFixed(2)}%`,
           `Método: ${confidence.method || "—"}`,
           `Consenso: ${confidence.consensus?.status || "—"}`,
           `Votos: ${confidence.consensus?.winning_votes || 0}/${confidence.consensus?.participants || 0}`,
           aiSuggestion?.accepted ? `Sugestão IA: ${aiSuggestion.answer} (${Number(aiSuggestion.confidence_percent || 0).toFixed(1)}%)` : null,
           `Fill: ${Number(confidence.fill_percent || 0).toFixed(1)}%`,
           `Contraste: ${Number(confidence.contrast || 0).toFixed(2)}`,
           `Margem: ${Number(confidence.margin_percent || 0).toFixed(1)}%`,
           `Qualidade local: ${confidence.local_quality || "—"}`].filter(Boolean).join(" · ")
        : "";
      return (
        <div
          key={key}
          data-testid={`answer-${key}`}
          title={confidenceTitle}
          className={`flex items-center gap-2 border p-2 transition-colors ${aiSuggestion?.accepted ? "outline outline-1 outline-primary/40" : ""} ${
            confidence?.review_required
              ? "border-destructive bg-destructive/10 ring-1 ring-destructive/30"
              : confidence?.review_status === "warning"
                ? "border-[hsl(var(--warning))]/70 bg-[hsl(var(--warning))]/5"
                : (tone || "border-border hover:border-foreground")
          }`}
        >
          <span className="question-number w-6">
            {prefix ? `P${q}` : String(q).padStart(2, "0")}
          </span>
          <span className="answer-chip" data-marked={!!val} data-empty={!val}>
            {val || "—"}
          </span>
          {aiSuggestion?.accepted && (
            <span className="text-[8px] font-mono-data font-bold text-primary" title={aiSuggestion.evidence || "Sugestão da IA"}>
              IA {aiSuggestion.answer}
            </span>
          )}
          {confidence && (
            <span className={`ml-auto text-[9px] font-mono-data ${
              Number(confidence.confidence_percent || 0) >= 90
                ? "text-[hsl(var(--success))]"
                : Number(confidence.confidence_percent || 0) >= 80
                  ? "text-[hsl(var(--warning))]"
                  : "text-destructive"
            }`}>
              {Number(confidence.confidence_percent || 0).toFixed(0)}%
            </span>
          )}
        </div>
      );
    })}
  </div>
);

/* -------------------- Editable chip row -------------------- */
const EditChipRow = ({ questions, answers, letters, prefix = "", onChange }) => (
  <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
    {questions.map((q) => {
      const key = prefix ? `${prefix}${q}` : String(q);
      const val = answers[key];
      return (
        <div
          key={key}
          data-testid={`edit-answer-${key}`}
          className="border border-border p-2 hover:border-foreground transition-colors"
        >
          <div className="question-number mb-1">
            {prefix ? `P${q}` : String(q).padStart(2, "0")}
          </div>
          <div className="flex gap-0.5">
            {letters.map((letter) => (
              <button
                key={letter}
                data-testid={`edit-btn-${key}-${letter}`}
                onClick={() => onChange(key, val === letter ? null : letter)}
                className={`flex-1 text-xs font-mono-data font-semibold border transition-colors ${
                  val === letter
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-foreground hover:border-foreground"
                }`}
                style={{ minHeight: "22px" }}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

/* -------------------- Editable CPF -------------------- */
const EditableCPF = ({ cpf, onChange }) => {
  const digits = cpf.padEnd(11, "?").split("").slice(0, 11);
  const setDigit = (idx, val) => {
    const arr = [...digits];
    arr[idx] = val;
    onChange(arr.join(""));
  };
  return (
    <div className="flex gap-1 flex-wrap" data-testid="cpf-edit-input">
      {digits.map((d, i) => (
        <select
          key={i}
          data-testid={`cpf-select-${i}`}
          value={d === "?" ? "" : d}
          onChange={(e) => setDigit(i, e.target.value || "?")}
          className={`w-9 h-11 border font-mono-data font-bold text-lg text-center bg-card cursor-pointer focus:outline-none focus:border-primary ${
            d === "?" ? "border-destructive text-destructive" : "border-border"
          }`}
        >
          <option value="">?</option>
          {"0123456789".split("").map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      ))}
    </div>
  );
};

/* -------------------- Reprocess dialog -------------------- */
const ReprocessDialog = ({ onDone }) => {
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [fill, setFill] = useState([0.22]);
  const [dominance, setDominance] = useState([1.25]);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/omr/corrections/${id}/reprocess`, {
        fill_threshold: fill[0],
        dominance: dominance[0],
      });
      toast.success(
        `Reprocessado: ${res.data.marked_objective}/100 marcadas · CPF ${res.data.cpf}`,
      );
      onDone(res.data);
      setOpen(false);
    } catch (e) {
      toast.error(
        "Falha ao reprocessar: " + (e?.response?.data?.detail || e.message),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="reprocess-btn"
          variant="outline"
          className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs"
        >
          <ArrowClockwise size={14} weight="bold" className="mr-2" />
          Reprocessar
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-md" aria-describedby="reprocess-desc">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight">
            Reprocessar com parâmetros
          </DialogTitle>
        </DialogHeader>
        <p id="reprocess-desc" className="sr-only">
          Re-execute o OMR com sensibilidade e dominância customizadas.
        </p>
        <div className="space-y-6 py-4">
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <span className="label-eyebrow">sensibilidade (fill min)</span>
              <span className="font-mono-data text-sm font-bold">
                {fill[0].toFixed(2)}
              </span>
            </div>
            <Slider
              data-testid="fill-slider"
              value={fill}
              onValueChange={setFill}
              min={0.1}
              max={0.45}
              step={0.01}
            />
            <div className="mt-1 text-xs text-muted-foreground font-mono-data">
              Menor = mais permissivo (pega marcas mais faint)
            </div>
          </div>
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <span className="label-eyebrow">dominância</span>
              <span className="font-mono-data text-sm font-bold">
                {dominance[0].toFixed(2)}
              </span>
            </div>
            <Slider
              data-testid="dominance-slider"
              value={dominance}
              onValueChange={setDominance}
              min={1.0}
              max={2.0}
              step={0.05}
            />
            <div className="mt-1 text-xs text-muted-foreground font-mono-data">
              Quão dominante o top deve ser vs. 2º lugar
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setOpen(false)}
            data-testid="reprocess-cancel"
          >
            Cancelar
          </Button>
          <Button
            data-testid="reprocess-run"
            onClick={run}
            disabled={loading}
            className="rounded-none"
          >
            {loading ? "Processando…" : "Executar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------- Main page -------------------- */
export default function DetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completingReview, setCompletingReview] = useState(false);
  const [examOptions, setExamOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [visualMode, setVisualMode] = useState("preview");
  const [aiOcrStatus, setAiOcrStatus] = useState(null);
  const [aiOcrRunning, setAiOcrRunning] = useState(false);
  const [deletingCorrection, setDeletingCorrection] = useState(false);
  const [studentLinkDialogOpen, setStudentLinkDialogOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentCandidates, setStudentCandidates] = useState([]);
  const [studentCandidateTotal, setStudentCandidateTotal] = useState(0);
  const [studentCandidateContext, setStudentCandidateContext] = useState(null);
  const [searchingStudentCandidates, setSearchingStudentCandidates] = useState(false);
  const [linkingStudentId, setLinkingStudentId] = useState("");
  const [studentNameCropUnavailable, setStudentNameCropUnavailable] = useState(false);
  const loadStudentCandidates = useCallback(async (query = "", correctionId = id, correction = data) => {
    const normalizedQuery = String(query || "").trim();
    // Start with the widest roster that the protected endpoint permits.  The
    // server still enforces the correction's class/school scope; this merely
    // means an operator does not have to guess a name before seeing options.
    // Administrators can review the whole registered roster on an unlinked
    // card. The protected server remains the permission boundary; this only
    // removes the former hidden 500-name cap from manual recovery.
    const params = { q: normalizedQuery || undefined, limit: 5000 };
    const regularEndpoint = `/omr/corrections/${correctionId}/student-candidates`;
    setSearchingStudentCandidates(true);
    setStudentNameCropUnavailable(false);
    try {
      let response;
      let usedVisualReview = false;
      try {
        response = await api.get(`/omr/corrections/${correctionId}/student-name-review`, { params });
        usedVisualReview = true;
      } catch (reviewError) {
        // A layout without a calibrated name rectangle, or a temporary
        // frontend/backend mismatch, must not block a permitted manual link.
        if (![404, 409].includes(reviewError?.response?.status)) throw reviewError;
        response = await api.get(regularEndpoint, { params });
      }

      const payload = response.data;
      const candidates = Array.isArray(payload)
        ? payload
        : (payload?.candidates || payload?.students || payload?.results || payload?.items || []);
      const reviewOcr = payload?.ocr || {};
      const reviewContext = usedVisualReview && !Array.isArray(payload)
        ? {
            ...payload,
            identity_visual_review: true,
            special_name_review: true,
            student_name_crop: payload?.name_crop || null,
            recognized_name: reviewOcr.recognized_name ?? payload?.recognized_name ?? "",
            raw_ocr_text: reviewOcr.raw_text ?? payload?.raw_ocr_text ?? "",
            ocr_status: reviewOcr.status ?? payload?.ocr_status ?? "",
            recognized_confidence: reviewOcr.confidence ?? payload?.recognized_confidence ?? 0,
            ocr_crop_strategy: reviewOcr.crop_strategy ?? payload?.ocr_crop_strategy ?? "",
          }
        : payload;
      setStudentCandidates(Array.isArray(candidates) ? candidates : []);
      setStudentCandidateTotal(Number(payload?.total ?? payload?.count ?? candidates?.length ?? 0));
      setStudentCandidateContext(Array.isArray(payload) ? null : reviewContext);
    } catch (error) {
      // This compatibility fallback only applies while an older backend is
      // temporarily serving the UI. The current endpoint enforces the
      // correction's exact access scope before returning candidates.
      if (error?.response?.status === 404) {
        try {
          const legacy = await api.get("/students", { params: { q: normalizedQuery || undefined } });
          const candidates = Array.isArray(legacy.data) ? legacy.data : (legacy.data?.results || []);
          setStudentCandidates(candidates.slice(0, 50));
          setStudentCandidateTotal(candidates.length);
          setStudentCandidateContext(null);
        } catch (legacyError) {
          toast.error("Falha ao procurar alunos: " + (legacyError?.response?.data?.detail || legacyError.message));
          setStudentCandidates([]);
          setStudentCandidateTotal(0);
          setStudentCandidateContext(null);
        }
      } else {
        toast.error("Falha ao procurar alunos: " + (error?.response?.data?.detail || error.message));
        setStudentCandidates([]);
        setStudentCandidateTotal(0);
        setStudentCandidateContext(null);
      }
    } finally {
      setSearchingStudentCandidates(false);
    }
  }, [data, id]);

  const openStudentLinkDialog = useCallback((correction = data) => {
    if (!correction?.id || !correction?.can_edit) return;
    setStudentNameCropUnavailable(false);
    setStudentCandidateContext(null);
    setStudentCandidates([]);
    setStudentCandidateTotal(0);
    // The OCR reading stays visible as a hint, but it must never hide the
    // available roster.  A blank initial search deliberately loads the list.
    setStudentSearch("");
    setStudentLinkDialogOpen(true);
    loadStudentCandidates("", correction.id, correction);
  }, [data, loadStudentCandidates]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/omr/corrections/${id}`);
      setData(res.data);
    } catch {
      toast.error("Correção não encontrada");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const loadOptions = async () => {
      if (user?.student_id) return;
      try {
        const [examsRes, classesRes, aiStatusRes] = await Promise.all([api.get("/exams"), api.get("/classes"), api.get("/ai-ocr/status")]);
        setExamOptions(examsRes.data || []);
        setClassOptions(classesRes.data || []);
        setAiOcrStatus(aiStatusRes.data || null);
      } catch (error) {
        // silently ignore auxiliary option loading errors
      }
    };
    loadOptions();
    // eslint-disable-next-line
  }, [id, user?.student_id]);

  const buildDraft = () => ({
      cpf: data.cpf || "?".repeat(11),
      answers: { ...data.answers },
      perception: { ...data.perception },
      exam_id: data.exam_id || "",
      class_id: data.class_id || "",
      ai_ocr_accepted_questions: [],
  });

  const startEdit = () => {
    setDraft(buildDraft());
    setEditing(true);
  };

  const runAiOcr = async () => {
    setAiOcrRunning(true);
    try {
      const response = await api.post(`/omr/corrections/${id}/ai-ocr`);
      const report = response.data || {};
      if (report.status === "completed") {
        toast.success(`OCR por IA concluído: ${report.suggestion_count || 0} sugestão(ões) segura(s)`);
      } else if (report.status === "skipped") {
        toast.info("Nenhuma questão elegível para o OCR por IA");
      } else {
        toast.error("OCR por IA não concluiu: " + (report.error || report.reason || "falha desconhecida"));
      }
      await load();
    } catch (error) {
      toast.error("Falha no OCR por IA: " + (error?.response?.data?.detail || error.message));
    } finally {
      setAiOcrRunning(false);
    }
  };

  const useAiSuggestions = () => {
    const next = buildDraft();
    let applied = 0;
    Object.entries(data.ai_ocr?.suggestions || {}).forEach(([question, suggestion]) => {
      if (suggestion?.accepted && suggestion.answer && !next.answers[question]) {
        next.answers[question] = suggestion.answer;
        next.ai_ocr_accepted_questions.push(Number(question));
        applied += 1;
      }
    });
    setDraft(next);
    setEditing(true);
    toast.info(`${applied} lacuna(s) preenchida(s) no editor. Confira antes de salvar.`);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/omr/corrections/${id}`, draft);
      setData(res.data);
      setEditing(false);
      setDraft(null);
      toast.success("Alterações salvas");
    } catch (e) {
      toast.error("Erro ao salvar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const completeReview = async () => {
    const reasons = data.manual_review_reasons || {};
    const stillBlank = Number(reasons.objective_blank_count || 0) + Number(reasons.perception_blank_count || 0);
    if (stillBlank && !window.confirm(
      `Ainda existem ${stillBlank} célula(s) vazia(s). Confirmar que o cartão foi conferido manualmente mesmo assim?`,
    )) return;
    setCompletingReview(true);
    try {
      await api.post(`/omr/corrections/${id}/review`, { action: "complete" });
      toast.success("Revisão manual concluída");
      await load();
    } catch (e) {
      toast.error("Erro ao concluir revisão: " + (e?.response?.data?.detail || e.message));
    } finally {
      setCompletingReview(false);
    }
  };

  const deleteCorrection = async () => {
    if (!window.confirm("Apagar definitivamente este cartão e sua correção?")) return;
    setDeletingCorrection(true);
    try {
      await api.delete(`/omr/corrections/${id}`);
      toast.success("Cartão e correção apagados");
      navigate(backTarget);
    } catch (error) {
      toast.error("Falha ao apagar: " + (error?.response?.data?.detail || error.message));
      setDeletingCorrection(false);
    }
  };

  const linkStudentCandidate = async (student) => {
    if (!student?.id) return;
    setLinkingStudentId(student.id);
    try {
      const response = await api.post(`/omr/corrections/${id}/student-resolution`, {
        mode: "existing",
        student_id: student.id,
      });
      toast.success(`Cartão vinculado a ${response.data?.student?.name || student.name || "aluno"}`);
      setStudentLinkDialogOpen(false);
      await load();
    } catch (error) {
      toast.error("Falha ao vincular aluno: " + (error?.response?.data?.detail || error.message));
    } finally {
      setLinkingStudentId("");
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-16 label-eyebrow">
        carregando…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-16">
        <div className="label-eyebrow">registro não encontrado</div>
        <Link
          to="/exams"
          className="text-primary text-sm font-mono-data hover:underline mt-4 inline-block"
        >
          ← Voltar para as provas
        </Link>
      </div>
    );
  }

  const cpfDisplay = editing ? draft.cpf : data.cpf || "";
  const cpfDigits = cpfDisplay.padEnd(11, "?").split("").slice(0, 11);
  const answers = editing ? draft.answers : data.answers;
  const perception = editing ? draft.perception : data.perception;
  const fromStudent = searchParams.get("from") === "student" && searchParams.get("student_id");
  const fromReview = searchParams.get("review") === "1";
  const backTarget = fromStudent
    ? `/students/${encodeURIComponent(searchParams.get("student_id"))}`
    : data.exam_id
      ? `/exams?open=${encodeURIComponent(data.exam_id)}${fromReview ? "&tab=review" : ""}`
      : "/exams";
  const backLabel = fromStudent ? "Voltar ao aluno" : data.exam_id ? "Voltar à prova" : "Voltar às provas";

  const onEditAnswer = (key, value) => {
    setDraft({ ...draft, answers: { ...draft.answers, [key]: value } });
  };
  const onEditPerc = (key, value) => {
    setDraft({ ...draft, perception: { ...draft.perception, [key]: value } });
  };

  const selectedExamMeta = editing ? examOptions.find((item) => item.id === draft.exam_id) : null;
  const activeExamMeta = selectedExamMeta || data.exam || null;
  const objectiveCount = Number(activeExamMeta?.question_count || data.layout_question_count || 100);
  const perceptionCount = Number(activeExamMeta?.perception_count ?? data.layout_perception_count ?? 10);
  const allowedClassIds = selectedExamMeta
    ? (selectedExamMeta.class_ids || [selectedExamMeta.class_id]).filter(Boolean)
    : [];
  const visibleClassOptions = selectedExamMeta
    ? classOptions.filter((item) => allowedClassIds.includes(item.id))
    : classOptions;
  const autoReview = data.omr_diagnostics?.auto_review || null;
  const consensus = data.omr_diagnostics?.consensus_engine || null;
  const reviewQuestions = autoReview?.review_questions || data.manual_review_reasons?.objective_review_questions || [];
  const aiFillableCount = Object.entries(data.ai_ocr?.suggestions || {}).filter(
    ([question, suggestion]) => suggestion?.accepted && suggestion.answer && !data.answers?.[question],
  ).length;
  const hasUnlinkedStudent = data.can_edit && !data.student_id;
  const nameOcr = data.student_name_ocr || {};
  const specialReviewOcr = studentCandidateContext?.ocr || {};
  const recognizedName = String(nameOcr.text || specialReviewOcr.recognized_name || studentCandidateContext?.recognized_name || data.student_name_snapshot || "").trim();
  const rawRecognizedName = String(nameOcr.raw_text || specialReviewOcr.raw_text || studentCandidateContext?.raw_ocr_text || "").trim();
  const nameOcrStatus = String(nameOcr.status || specialReviewOcr.status || studentCandidateContext?.ocr_status || data.student_resolution_issue || "pendente").trim();
  const nameOcrConfidence = Number(nameOcr.confidence_percent ?? nameOcr.confidence ?? specialReviewOcr.confidence ?? studentCandidateContext?.recognized_confidence ?? 0);
  const ocrCropStrategy = String(specialReviewOcr.crop_strategy || studentCandidateContext?.ocr_crop_strategy || nameOcr.crop_strategy || "").trim();
  const identityVisualReview = isIdentityVisualReview(data, studentCandidateContext);
  const studentNameCropSrc = resolveStudentNameCrop(data, studentCandidateContext);
  const showStudentNameCrop = identityVisualReview && Boolean(studentNameCropSrc) && !studentNameCropUnavailable;
  const candidateCaption = studentCandidateTotal > studentCandidates.length
    ? `Mostrando os primeiros ${studentCandidates.length} de ${studentCandidateTotal} aluno(s) disponíveis.`
    : studentSearch
      ? `${studentCandidateTotal || studentCandidates.length} aluno(s) encontrado(s) para este filtro.`
      : `${studentCandidateTotal || studentCandidates.length} aluno(s) disponível(is) para vinculação.`;

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <Link
        to={backTarget}
        data-testid="back-to-exam"
        className="inline-flex items-center gap-2 label-eyebrow hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft size={14} weight="bold" /> {backLabel}
      </Link>

      {data.can_edit && data.manual_review_status === "pending" && (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4">
          <Warning size={26} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />
          <div className="flex-1">
            <div className="font-display text-xl font-black tracking-tight">Revisão seletiva necessária</div>
            <div className="text-sm text-muted-foreground mt-1">
              {reviewQuestions.length > 0
                ? <>Revise somente {reviewQuestions.length} questão(ões): <strong>{reviewQuestions.map((q) => `Q${q}`).join(", ")}</strong>.</>
                : <>Há campos vazios que precisam de confirmação.</>}
              {data.manual_review_reasons?.perception_blank_count > 0 && (
                <> {data.manual_review_reasons.perception_blank_count} campo(s) de percepção também estão vazios.</>
              )}
            </div>
          </div>
          <Button
            onClick={completeReview}
            disabled={editing || completingReview}
            className="rounded-none font-mono-data uppercase tracking-widest text-xs shrink-0"
          >
            <CheckCircle size={15} weight="bold" className="mr-2" />
            {completingReview ? "Concluindo…" : "Concluir revisão"}
          </Button>
        </div>
      )}

      {data.manual_review_status === "completed" && fromReview && (
        <div className="border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 p-4 mb-6 flex items-center gap-3">
          <CheckCircle size={20} weight="fill" className="text-[hsl(var(--success))]" />
          <div className="text-sm font-semibold">Este cartão já teve a revisão manual concluída.</div>
        </div>
      )}

      {hasUnlinkedStudent && (
        <section className="border-2 border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-5 mb-6 flex flex-col lg:flex-row lg:items-center gap-5" data-testid="unlinked-student-card-panel">
          <div className="h-12 w-12 shrink-0 flex items-center justify-center bg-[hsl(var(--warning))] text-black">
            <Warning size={27} weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow text-[hsl(var(--warning))] mb-1">identificação pendente</div>
            <div className="font-display text-2xl font-black tracking-tight">Sem aluno vinculado</div>
            <div className="text-sm text-muted-foreground mt-1">
              Abra a revisão para ver a lista de alunos disponíveis e confirmar quem respondeu este cartão. A nota e as respostas já corrigidas são preservadas.
            </div>
            {recognizedName && <div className="text-sm mt-3"><strong>Leitura sugerida:</strong> {recognizedName}</div>}
            {!recognizedName && rawRecognizedName && <div className="text-sm mt-3"><strong>Leitura parcial:</strong> {rawRecognizedName}</div>}
            <div className="text-xs font-mono-data text-muted-foreground mt-2">
              OCR: {nameOcrStatus}{nameOcrConfidence > 0 ? ` · confiança ${nameOcrConfidence.toFixed(1)}%` : ""}
              {data.student_resolution_status === "ignored" ? " · mantido sem vínculo anteriormente" : ""}
            </div>
          </div>
          <Button onClick={() => openStudentLinkDialog()} className="rounded-none shrink-0 min-h-12" data-testid="open-student-linker">
            <IdentificationCard size={18} weight="bold" className="mr-2" />
            {identityVisualReview ? "Revisar nome e vincular" : "Ver alunos e vincular"}
          </Button>
        </section>
      )}

      {data.can_edit && editing && (
        <div className="border border-border bg-card p-5 mb-6">
          <div className="label-eyebrow mb-3">alocação da correção</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-mono-data uppercase tracking-widest mb-2 text-muted-foreground">Prova</div>
              <select
                value={draft.exam_id || ""}
                onChange={(event) => {
                  const nextExamId = event.target.value;
                  const nextExam = examOptions.find((item) => item.id === nextExamId);
                  const nextClassIds = nextExam ? (nextExam.class_ids || [nextExam.class_id]).filter(Boolean) : [];
                  const nextClassId = nextClassIds.length === 1 ? nextClassIds[0] : (nextClassIds.includes(draft.class_id) ? draft.class_id : "");
                  setDraft({ ...draft, exam_id: nextExamId, class_id: nextClassId });
                }}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Sem prova vinculada</option>
                {examOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
              <div className="text-xs text-muted-foreground mt-2">Use esta opção quando o QR não foi reconhecido ou quando deseja mover o cartão para outra prova.</div>
            </div>
            <div>
              <div className="text-xs font-mono-data uppercase tracking-widest mb-2 text-muted-foreground">Turma</div>
              <select
                value={draft.class_id || ""}
                onChange={(event) => setDraft({ ...draft, class_id: event.target.value })}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Sem turma definida</option>
                {visibleClassOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
              <div className="text-xs text-muted-foreground mt-2">Ao escolher uma prova com apenas uma turma, a turma será sugerida automaticamente.</div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-8 border border-border bg-card p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-eyebrow mb-2">arquivo processado</div>
              <div className="font-display text-2xl font-bold tracking-tight break-all">
                {data.filename}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {data.can_edit && (!editing ? (
                <>
                  <ReprocessDialog onDone={setData} />
                  {data.can_delete && (
                    <Button
                      data-testid="delete-correction-btn"
                      variant="outline"
                      onClick={deleteCorrection}
                      disabled={deletingCorrection}
                      className="rounded-none border-destructive text-destructive font-mono-data uppercase tracking-widest text-xs"
                    >
                      <Trash size={14} weight="bold" className="mr-2" />
                      {deletingCorrection ? "Apagando…" : "Apagar cartão"}
                    </Button>
                  )}
                  {aiOcrStatus?.available && (
                    <Button
                      data-testid="ai-ocr-btn"
                      variant="outline"
                      onClick={runAiOcr}
                      disabled={aiOcrRunning}
                      className="rounded-none border-primary/50 text-primary font-mono-data uppercase tracking-widest text-xs"
                    >
                      <Sparkle size={14} weight="fill" className="mr-2" />
                      {aiOcrRunning ? "Analisando…" : "OCR por IA"}
                    </Button>
                  )}
                  <Button
                    data-testid="edit-btn"
                    onClick={startEdit}
                    className="rounded-none font-mono-data uppercase tracking-widest text-xs"
                  >
                    <PencilSimple size={14} weight="bold" className="mr-2" />
                    Editar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    data-testid="cancel-edit-btn"
                    variant="outline"
                    onClick={cancelEdit}
                    className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs"
                  >
                    <XCircle size={14} weight="bold" className="mr-2" />
                    Cancelar
                  </Button>
                  <Button
                    data-testid="save-edit-btn"
                    onClick={saveEdit}
                    disabled={saving}
                    className="rounded-none font-mono-data uppercase tracking-widest text-xs"
                  >
                    <FloppyDisk size={14} weight="bold" className="mr-2" />
                    {saving ? "Salvando…" : "Salvar"}
                  </Button>
                </>
              ))}
            </div>
          </div>
          <div className="mt-6 flex items-center gap-6 text-sm">
            <div>
              <span className="label-eyebrow">Data</span>
              <div className="font-mono-data mt-1">
                {new Date(data.created_at).toLocaleString("pt-BR")}
              </div>
            </div>
            {data.edited_at && (
              <div>
                <span className="label-eyebrow">Editado</span>
                <div className="font-mono-data mt-1 text-primary">
                  {new Date(data.edited_at).toLocaleString("pt-BR")}
                </div>
              </div>
            )}
            {data.reprocessed_at && (
              <div>
                <span className="label-eyebrow">Reprocessado</span>
                <div className="font-mono-data mt-1 text-primary">
                  {new Date(data.reprocessed_at).toLocaleString("pt-BR")}
                </div>
              </div>
            )}
            <div>
              <span className="label-eyebrow">Layout</span>
              <div className="font-mono-data mt-1 text-primary">
                {data.omr_diagnostics?.layout?.name || data.layout_id || "SAKI/Xm Oficial A4 · 100 questões"}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 border border-border bg-card p-8">
          <div className="flex items-center gap-2 mb-4">
            <IdentificationCard
              size={20}
              weight="duotone"
              className="text-primary"
            />
            <span className="label-eyebrow">cpf extraído</span>
          </div>
          {editing ? (
            <EditableCPF
              cpf={draft.cpf}
              onChange={(v) => setDraft({ ...draft, cpf: v })}
            />
          ) : (
            <div className="flex gap-1 flex-wrap" data-testid="cpf-display">
              {cpfDigits.map((d, i) => (
                <div
                  key={i}
                  className={`w-8 h-10 border flex items-center justify-center font-mono-data font-bold text-lg ${
                    d === "?"
                      ? "border-destructive text-destructive bg-destructive/5"
                      : "border-border"
                  }`}
                >
                  {d}
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex gap-4 text-xs font-mono-data text-muted-foreground">
            <div>
              <div className="label-eyebrow mb-1">marcadas</div>
              <div className="text-2xl font-black text-foreground font-display">
                {editing
                  ? Object.values(draft.answers).filter(Boolean).length
                  : data.marked_objective}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">vazias</div>
              <div className="text-2xl font-black text-foreground font-display">
                {editing
                  ? objectiveCount - Object.values(draft.answers).filter(Boolean).length
                  : data.unanswered_objective}
              </div>
            </div>
            {data.score_details && !editing && (
              <>
                <div>
                  <div className="label-eyebrow mb-1">acertos</div>
                  <div className="text-2xl font-black text-[hsl(var(--success))] font-display" data-testid="score-hits">
                    {data.score_details.hits}
                    <span className="text-sm text-muted-foreground font-mono-data ml-1">
                      /{data.score_details.total_valid}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow mb-1">nota</div>
                  <div
                    className={`text-2xl font-black font-display ${
                      data.score_details.score >= 70
                        ? "text-[hsl(var(--success))]"
                        : data.score_details.score >= 50
                        ? "text-[hsl(var(--warning))]"
                        : "text-destructive"
                    }`}
                    data-testid="score-pct"
                  >
                    {data.score_details.score.toFixed(1)}%
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Split panes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Preview */}
        <div className="border border-border bg-card">
          <div className="px-6 py-3 border-b border-border">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="label-eyebrow inline-flex items-center gap-2"><MapTrifold size={16} /> inteligência visual V36</div>
              <div className="flex items-center gap-3 text-[10px] font-mono-data">
                <span className="inline-flex items-center gap-1"><Circle size={8} weight="fill" style={{ color: "#30B430" }} /> alta</span>
                <span className="inline-flex items-center gap-1"><Circle size={8} weight="fill" style={{ color: "#FFD700" }} /> média</span>
                <span className="inline-flex items-center gap-1"><Circle size={8} weight="fill" style={{ color: "#DC2323" }} /> baixa</span>
              </div>
            </div>
            {!data.viewer_is_student && <div className="flex flex-wrap gap-2">
              {[
                ["preview", "Original"],
                ["confidence", "Confiança"],
                ["method", "Método"],
                ["contrast", "Contraste"],
                ["fill", "Fill"],
              ].map(([mode, label]) => (
                <button
                  type="button"
                  key={mode}
                  disabled={mode !== "preview" && !data.heatmaps_b64?.[mode]}
                  onClick={() => setVisualMode(mode)}
                  className={`border px-3 py-1 text-[10px] font-mono-data uppercase tracking-widest transition-colors ${visualMode === mode ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground disabled:opacity-40"}`}
                >
                  {label}
                </button>
              ))}
            </div>}
          </div>
          <div className="p-4 bg-muted/20">
            {(visualMode === "preview" ? (data.source_image_available || data.preview_png_b64) : data.heatmaps_b64?.[visualMode]) ? (
              <img
                data-testid="preview-image"
                src={visualMode === "preview" && data.source_image_available
                  ? `${API}/omr/corrections/${id}/image`
                  : `data:image/png;base64,${visualMode === "preview" ? data.preview_png_b64 : data.heatmaps_b64?.[visualMode]}`}
                alt={visualMode === "preview" ? "Cartão-resposta processado" : `Heatmap de ${visualMode}`}
                className="w-full h-auto border border-border"
              />
            ) : (
              <div className="p-16 text-center label-eyebrow">
                preview indisponível
              </div>
            )}
          </div>
        </div>

        {/* Answers */}
        <div className="space-y-6">
          {data.can_edit && !editing && data.ai_ocr && data.ai_ocr.status !== "superseded" && (
            <div className="border border-primary/50 bg-primary/5 p-5" data-testid="ai-ocr-panel">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow text-primary inline-flex items-center gap-2"><Sparkle size={15} weight="fill" /> OCR assistido por IA · V41</div>
                  <div className="font-display text-2xl font-black tracking-tight mt-1">
                    {data.ai_ocr.status === "completed" ? `${data.ai_ocr.suggestion_count || 0} sugestão(ões) para conferência` : "Assistência sem sugestão aplicável"}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Somente linhas objetivas problemáticas foram analisadas; CPF, QR e identificação não foram enviados. Nenhuma resposta foi alterada automaticamente.
                  </p>
                </div>
                {aiFillableCount > 0 && (
                  <Button variant="outline" className="rounded-none shrink-0" onClick={useAiSuggestions}>
                    Preencher lacunas no editor
                  </Button>
                )}
              </div>
              {!!Object.keys(data.ai_ocr.suggestions || {}).length && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.entries(data.ai_ocr.suggestions || {}).map(([question, suggestion]) => (
                    <div key={question} className={`border px-3 py-2 text-xs font-mono-data ${suggestion.accepted ? "border-primary bg-card" : "border-border text-muted-foreground"}`} title={suggestion.evidence || ""}>
                      Q{question} · {suggestion.answer || "ambígua"} · {Number(suggestion.confidence_percent || 0).toFixed(1)}%
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10px] font-mono-data text-muted-foreground mt-4">
                Modelo {data.ai_ocr.model || "—"} · modo {data.ai_ocr.trigger || "manual"} · imagem anonimizada
              </div>
            </div>
          )}
          <div className="border border-border bg-card">
            <div className="px-6 py-3 border-b border-border flex items-center gap-2">
              <ListChecks size={18} weight="duotone" className="text-primary" />
              <span className="label-eyebrow">{objectiveCount} questões objetivas</span>
              {!data.viewer_is_student && !editing && autoReview && (
                <div className="ml-auto flex items-center gap-3">
                  <span className={`text-xs font-mono-data font-semibold ${autoReview.auto_approved ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                    {autoReview.auto_approved
                      ? `correção automática · ${Number(autoReview.automation_percent || 0).toFixed(2)}%`
                      : `${autoReview.review_count || 0} questão(ões) para revisar`}
                  </span>
                  {!autoReview.auto_approved && (
                    <button
                      type="button"
                      onClick={() => setReviewOnly((value) => !value)}
                      className="border border-border px-2 py-1 text-[10px] font-mono-data uppercase tracking-widest hover:border-foreground"
                    >
                      {reviewOnly ? "mostrar todas" : "mostrar só dúvidas"}
                    </button>
                  )}
                </div>
              )}
              {!data.viewer_is_student && !editing && !autoReview && data.omr_diagnostics?.confidence_engine && (
                <span className="ml-auto text-xs font-mono-data text-muted-foreground">
                  confiança da página: {Number(data.omr_diagnostics.confidence_engine.overall_percent || 0).toFixed(2)}%
                </span>
              )}
              {editing && (
                <span className="label-eyebrow text-primary ml-auto">
                  · modo edição
                </span>
              )}
            </div>
            <div className="p-6">
              {!data.viewer_is_student && !editing && consensus && (
                <div className="mb-5 border border-border bg-muted/20 p-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-mono-data">
                  <span className="font-semibold uppercase tracking-wider">AI Consensus V39</span>
                  <span className="text-[hsl(var(--success))]">{consensus.confirmed || 0} confirmadas</span>
                  <span className="text-primary">{consensus.recovered || 0} recuperadas</span>
                  <span className="text-muted-foreground">{consensus.compatibility_fallback || 0} fallback seguro</span>
                  <span className={consensus.unresolved ? "text-destructive" : "text-muted-foreground"}>
                    {consensus.unresolved || 0} sem consenso
                  </span>
                  <span className="ml-auto text-muted-foreground">
                    acordo forte: {Number(consensus.agreement_percent || 0).toFixed(2)}%
                  </span>
                </div>
              )}
              {editing ? (
                <EditChipRow
                  questions={Array.from({ length: objectiveCount }, (_, i) => i + 1)}
                  answers={answers}
                  letters={OBJ_LETTERS}
                  onChange={onEditAnswer}
                />
              ) : (
                <ReadChipRow
                  questions={Array.from({ length: objectiveCount }, (_, i) => i + 1)}
                  answers={answers}
                  examKey={data.exam?.answers}
                  annulled={data.exam?.annulled ? new Set(data.exam.annulled) : null}
                  diagnostics={data.viewer_is_student ? null : data.omr_diagnostics?.objective}
                  reviewOnly={reviewOnly}
                  aiSuggestions={data.viewer_is_student ? null : data.ai_ocr?.suggestions}
                />
              )}
            </div>
          </div>

          {perceptionCount > 0 && (
            <div className="border border-border bg-card">
              <div className="px-6 py-3 border-b border-border flex items-center gap-2">
                <Question size={18} weight="duotone" className="text-primary" />
                <span className="label-eyebrow">
                  pesquisa de percepção (p1–p{perceptionCount})
                </span>
              </div>
              <div className="p-6">
                {editing ? (
                  <EditChipRow
                    questions={Array.from({ length: perceptionCount }, (_, i) => i + 1)}
                    answers={perception}
                    letters={PERC_LETTERS}
                    prefix="P"
                    onChange={onEditPerc}
                  />
                ) : (
                  <ReadChipRow
                    questions={Array.from({ length: perceptionCount }, (_, i) => i + 1)}
                    answers={perception}
                    prefix="P"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={studentLinkDialogOpen}
        onOpenChange={(open) => setStudentLinkDialogOpen(open)}
      >
        <DialogContent className="rounded-none max-w-5xl max-h-[94vh] overflow-y-auto" aria-describedby="student-linker-description">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black tracking-tight">
              {identityVisualReview ? "Revisar identificação do aluno" : "Vincular cartão a um aluno"}
            </DialogTitle>
          </DialogHeader>
          <div id="student-linker-description" className="text-sm text-muted-foreground">
            {identityVisualReview
              ? "Compare a foto do nome no gabarito com a lista autorizada e confirme o aluno correto. O cartão e as respostas não serão alterados."
              : "Escolha um aluno disponível para esta prova. O cartão e as respostas não serão alterados."}
          </div>

          <div className={identityVisualReview ? "grid gap-4 lg:grid-cols-[minmax(360px,0.95fr)_minmax(0,1.05fr)]" : ""}>
            {identityVisualReview && (
              <figure className="border-2 border-primary/40 bg-primary/5 p-4" data-testid="tpi-student-name-crop" data-review-kind="student-identity-crop">
                <figcaption className="label-eyebrow text-primary mb-3">foto do nome no gabarito</figcaption>
                {searchingStudentCandidates && !studentCandidateContext ? (
                  <div className="min-h-40 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                    <Spinner size={24} className="animate-spin text-primary" />
                    Preparando a foto do nome…
                  </div>
                ) : showStudentNameCrop ? (
                  <img
                    src={studentNameCropSrc}
                    alt="Foto do campo de nome no gabarito do aluno"
                    className="w-full min-h-40 max-h-72 object-contain bg-white border border-border"
                    onError={() => setStudentNameCropUnavailable(true)}
                  />
                ) : (
                  <div className="min-h-40 flex items-center text-sm text-muted-foreground leading-relaxed">
                    Não foi possível recuperar o recorte deste cartão. A lista completa de alunos disponíveis continua abaixo.
                  </div>
                )}
              </figure>
            )}

            <div className="border border-border bg-muted/20 p-4 text-sm space-y-1">
              {recognizedName ? (
                <div><span className="text-muted-foreground">Nome lido pelo OCR:</span> <strong>{recognizedName}</strong></div>
              ) : rawRecognizedName ? (
                <div><span className="text-muted-foreground">Leitura bruta do OCR:</span> <strong>{rawRecognizedName}</strong></div>
              ) : (
                <div><span className="text-muted-foreground">Nome lido pelo OCR:</span> não disponível</div>
              )}
              <div className="text-xs font-mono-data text-muted-foreground">
                Status {nameOcrStatus}{nameOcrConfidence > 0 ? ` · confiança ${nameOcrConfidence.toFixed(1)}%` : ""}{ocrCropStrategy ? ` · recorte ${ocrCropStrategy}` : ""}
              </div>
              {identityVisualReview && (
                <div className="pt-2 text-xs text-muted-foreground leading-relaxed">
                  A escolha é manual e auditável. Use a escrita visível no recorte como referência; o OCR é apenas uma sugestão.
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={studentSearch}
              onChange={(event) => setStudentSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  loadStudentCandidates(studentSearch);
                }
              }}
              placeholder="Filtrar a lista por nome ou CPF"
              className="rounded-none"
              autoFocus
              data-testid="student-link-search"
            />
            <Button
              type="button"
              variant="outline"
              className="rounded-none shrink-0"
              disabled={searchingStudentCandidates}
              onClick={() => loadStudentCandidates(studentSearch)}
              data-testid="student-link-search-submit"
            >
              {searchingStudentCandidates ? <Spinner size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
              <span className="ml-2">Buscar</span>
            </Button>
            {studentSearch && (
              <Button
                type="button"
                variant="outline"
                className="rounded-none shrink-0"
                disabled={searchingStudentCandidates}
                onClick={() => {
                  setStudentSearch("");
                  loadStudentCandidates("");
                }}
              >Mostrar todos</Button>
            )}
          </div>

          <div className="text-xs text-muted-foreground font-mono-data">
            {searchingStudentCandidates ? "Carregando lista de alunos…" : candidateCaption}
          </div>

          {identityVisualReview && (
            <div className="border-l-2 border-primary bg-primary/5 px-3 py-2 text-xs text-muted-foreground" data-testid="tpi-name-review-candidates">
              Lista de alunos disponíveis para esta turma/prova. Se houver uma semelhança calculada pelo OCR, ela aparece ao lado do aluno apenas como apoio visual.
            </div>
          )}

          <div className="border border-border divide-y divide-border max-h-[48vh] overflow-y-auto" data-testid="student-link-candidates">
            {searchingStudentCandidates ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando alunos…</div>
            ) : studentCandidates.length ? studentCandidates.map((student) => {
              const classes = Array.isArray(student.classes) ? student.classes : [];
              const schools = Array.isArray(student.schools) ? student.schools : [];
              const similarity = candidateNameSimilarityPercent(student);
              return (
                <button
                  key={student.id}
                  type="button"
                  disabled={Boolean(linkingStudentId)}
                  onClick={() => linkStudentCandidate(student)}
                  className="w-full p-4 text-left hover:bg-muted/40 transition-colors disabled:opacity-50"
                  data-testid={`student-link-candidate-${student.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-semibold break-words">{student.name || "Aluno sem nome"}</div>
                      <div className="text-xs text-muted-foreground font-mono-data mt-1">CPF {student.cpf || "não informado"}</div>
                      {!!classes.length && <div className="text-xs text-muted-foreground mt-1">{classes.map((item) => item.name).filter(Boolean).join(" · ")}</div>}
                      {!classes.length && !!schools.length && <div className="text-xs text-muted-foreground mt-1">{schools.map((item) => item.name).filter(Boolean).join(" · ")}</div>}
                    </div>
                    <span className="shrink-0 text-xs font-mono-data text-primary">
                      {linkingStudentId === student.id ? "Vinculando…" : identityVisualReview ? "Confirmar nome" : "Selecionar"}
                    </span>
                  </div>
                  {identityVisualReview && similarity !== null && (
                    <div className="mt-2 text-[10px] font-mono-data text-muted-foreground">
                      semelhança OCR: {similarity.toFixed(0)}%
                    </div>
                  )}
                </button>
              );
            }) : (
              <div className="p-6 text-sm text-muted-foreground">
                Nenhum aluno autorizado encontrado. Tente outro nome ou CPF.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setStudentLinkDialogOpen(false)}>Continuar sem vincular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
